import {
  Card,
  Group,
  NumberInput,
  Slider,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBell,
  IconBuildingStore,
  IconCalendarEvent,
  IconChartBar,
  IconClipboardCheck,
  IconClipboardList,
  IconFileText,
  IconGift,
  IconMail,
  IconMicrophone,
  IconPresentation,
  IconQrcode,
  IconStar,
  IconTag,
} from "@tabler/icons-react";

import { formatPct, roundOneDecimal, scoreColor } from "./utils";

const CATEGORY_ICONS = {
  agenda: IconCalendarEvent,
  sessions: IconPresentation,
  checkin: IconQrcode,
  registration: IconClipboardList,
  sponsors: IconBuildingStore,
  pages: IconFileText,
  polls: IconChartBar,
  surveys: IconClipboardCheck,
  speakers: IconMicrophone,
  giveaway: IconGift,
  session_ratings: IconStar,
  tags: IconTag,
  email: IconMail,
  push: IconBell,
};

function MetricRow({ metric, weight, computed, disabled, onChange }) {
  const ratio = (computed?.ratio ?? 0) * 100;
  const contribution = computed?.contribution ?? 0;
  const detail = disabled ? "Feature off" : `Usage ${Math.round(ratio)}%`;

  return (
    <Group gap="sm" wrap="nowrap" align="center">
      <Stack gap={2} style={{ flex: "0 0 38%", minWidth: 0 }}>
        <Text size="sm" fw={500} truncate c={disabled ? "dimmed" : undefined}>
          {metric.label}
        </Text>
        <Text size="11px" c="dimmed">
          {detail}
        </Text>
      </Stack>
      <Slider
        min={0}
        max={100}
        step={0.1}
        value={weight}
        onChange={onChange}
        disabled={disabled}
        label={(v) => `${v.toFixed(1)}%`}
        style={{ flex: 1 }}
        color="blue"
      />
      <NumberInput
        value={weight}
        min={0}
        max={100}
        step={0.1}
        decimalScale={1}
        fixedDecimalScale
        disabled={disabled}
        onChange={(v) => onChange(Number(v) || 0)}
        suffix="%"
        hideControls
        w={64}
        size="xs"
        styles={{
          input: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
        }}
      />
      <Text
        size="sm"
        fw={700}
        c={disabled ? "dimmed" : scoreColor(contribution * 5)}
        ta="right"
        style={{ width: 44, fontVariantNumeric: "tabular-nums" }}
      >
        {contribution.toFixed(1)}
      </Text>
    </Group>
  );
}

function CategoryCard({ category, weights, computed, onChangeMetric }) {
  const categoryTotal = roundOneDecimal(
    category.metrics.reduce((a, m) => a + (weights[m.key] ?? 0), 0)
  );
  const included = computed?.included;
  const exclusionLabel =
    computed && !included && category.featureFlag ? "Feature off" : null;
  const showEffective =
    included && Math.abs(computed.weightEffective - categoryTotal) > 0.05;
  const Icon = CATEGORY_ICONS[category.key];

  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      style={{
        opacity: included ? 1 : 0.55,
        borderColor: included
          ? "var(--mantine-color-blue-2)"
          : "var(--mantine-color-gray-3)",
        background: included
          ? "var(--mantine-color-body)"
          : "var(--mantine-color-gray-0)",
      }}
    >
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
            {Icon && (
              <ThemeIcon
                variant="light"
                color={included ? "blue" : "gray"}
                radius="sm"
                size="md"
              >
                <Icon size={16} />
              </ThemeIcon>
            )}
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Text fw={600} size="sm" truncate>
                {category.label}
              </Text>
              {exclusionLabel && (
                <Text size="10px" c="dimmed">
                  {exclusionLabel}
                </Text>
              )}
            </Stack>
          </Group>
          <Group gap={6} wrap="nowrap" align="baseline">
            <Text
              fw={700}
              size="sm"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {formatPct(categoryTotal)}
            </Text>
            {showEffective && (
              <Text
                size="xs"
                c="cyan"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                → {formatPct(computed.weightEffective)}
              </Text>
            )}
          </Group>
        </Group>
        <Stack gap="sm">
          {category.metrics.map((m) => (
            <MetricRow
              key={m.key}
              metric={m}
              weight={weights[m.key] ?? 0}
              computed={computed?.metrics.find((cm) => cm.key === m.key)}
              disabled={!included}
              onChange={(v) => onChangeMetric(m.key, v)}
            />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

export default CategoryCard;
